/**
 * References:
 * - https://github.com/adamsilver/nostyle/blob/master/src/components/autocomplete/Autocomplete.js
 */

/**
 * A custom element for enhancing text boxes into autocomplete widgets.
 * This should not be directly consumed -- it should be extended with getOptionAttributes and getOptions set on it.
 */
export default class BaseAutocomplete extends HTMLElement {
  constructor() {
    super();

    this.debounce = undefined;

    this.render = () => {
      if (!this.getOptionAttributes) {
        // biome-ignore lint/suspicious/noConsole: debugging
        console.warn(
          'getOptionAttributes is not provided; will act as a normal text input without autocomplete',
          this,
        );
        return;
      }

      if (!this.getOptions) {
        // biome-ignore lint/suspicious/noConsole: debugging
        console.warn('getOptions is not provided; will act as a normal text input without autocomplete', this);
        return;
      }

      if (this.querySelector('[role="listbox"]')) {
        return;
      }

      this.input = this.querySelector('input');

      this.enhanceInput();
      this.setupClearButton();
      this.createStatusBox();
    };
  }

  get showOptionsOnFocus() {
    return this.getAttribute('showOptionsOnFocus') || false;
  }

  set showOptionsOnFocus(val) {
    this.setAttribute('showOptionsOnFocus', val);
  }

  static get observedAttributes() {
    return ['showOptionsOnFocus'];
  }

  connectedCallback() {
    this.render();
  }

  /**
   * Emit a custom event
   * @param {string} type - The event type
   * @param {object} detail - Any details to pass along with the event
   */
  emit = (type, detail = {}) => {
    // Create a new event
    const event = new CustomEvent(`${this.localName}:${type}`, {
      bubbles: true,
      cancelable: true,
      detail: detail,
    });

    // Dispatch the event
    return this.dispatchEvent(event);
  };

  enhanceInput = () => {
    const label = this.querySelector('label');
    label.id = `${label.getAttribute('for')}-label`;

    const listbox = document.createElement(this.grouped ? 'div' : 'ul');
    listbox.setAttribute('role', 'listbox');
    listbox.setAttribute('aria-labelledby', label.id);
    listbox.id = `${this.input.getAttribute('id')}-listbox`;
    listbox.style.display = 'none';
    listbox.addEventListener('keydown', this.menuKeyDownHandler);
    listbox.addEventListener('click', this.menuClickHandler);
    this.appendChild(listbox);

    this.input.setAttribute('aria-owns', listbox.id);
    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-expanded', 'false');

    this.input.addEventListener('click', this.clickHandler);
    this.input.addEventListener('keydown', this.textBoxKeydownHandler);
    this.input.addEventListener('keyup', this.textBoxKeyupHandler);
    this.input.addEventListener('focus', this.textBoxFocusHandler);

    document.addEventListener('click', this.lightDismissHandler);

    if (this.entityIdInputName) {
      const hiddenEntityIdInput = document.createElement('input');
      hiddenEntityIdInput.name = this.entityIdInputName;
      hiddenEntityIdInput.type = 'hidden';
      this.appendChild(hiddenEntityIdInput);

      // We could update the API so that it just assumes that the find_type is category if the entity ID is provided.
      const hiddenFindTypeInput = document.createElement('input');
      hiddenFindTypeInput.name = 'find_type';
      hiddenFindTypeInput.type = 'hidden';
      this.appendChild(hiddenFindTypeInput);
    }
  };

  setupClearButton = () => {
    const btn = document.createElement('button');
    btn.classList.add('clear');
    btn.type = 'button';
    btn.setAttribute('aria-label', 'clear entry');
    btn.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" aria-hidden="true" focusable="false" height="1em" width="1em"><path d="M345 137c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-119 119L73 103c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l119 119L39 375c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l119-119L311 409c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-119-119L345 137z"></path></svg>';
    btn.style.display = 'none';
    btn.addEventListener('click', this.clearInput);
    this.querySelector('.input-group').append(btn);
  };

  createStatusBox = () => {
    const status = document.createElement('div');
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('role', 'status');
    status.classList.add('visually-hidden');
    this.appendChild(status);
  };

  showClearButton = () => {
    const clrBtn = this.querySelector('button.clear');

    // `this.input` is coming in as undefined sometimes for some reason
    if (!this.input) {
      this.input = this.querySelector('input');
    }

    if (this.input.value.trim().length > 0) {
      clrBtn.style.display = 'block';
    }
  };

  hideClearButton = () => {
    const clrBtn = this.querySelector('button.clear');
    clrBtn.style.display = 'none';
  };

  updateStatus = (resultCount) => {
    if (resultCount === 0) {
      this.querySelector('[role="status"]').innerText = 'No results.';
    } else {
      this.querySelector('[role="status"]').innerText = `${resultCount} results available.`;
    }

    // 👇 https://github.com/adamsilver/nostyle/issues/18
    setTimeout(() => {
      this.querySelector('[role="status"]').innerText = '';
    }, 500);
  };

  lightDismissHandler = (event) => {
    if (event.target.className === 'dialog-trigger') {
      return;
    }

    if (!event.composedPath().includes(this)) {
      this.hideMenu();
      this.removeTextBoxFocus();
    }
  };

  textBoxFocusHandler = () => {
    this.querySelector('.input-group').setAttribute('data-focused', 'true');

    if (this.showOptionsOnFocus) {
      this.getOptions(this.input.value).then((options) => {
        if (this.input.value.trim().length === 0 && options.length === 0) {
          return;
        }

        this.buildMenu(options);
        this.updateStatus(
          this.grouped ? options.reduce((acc, group) => acc + group.results.length, 0) : options.length,
        );
        this.showMenu();
      });
    }
  };

  removeTextBoxFocus = () => {
    this.querySelector('.input-group').removeAttribute('data-focused');
  };

  clickHandler = () => {
    this.clearOptions();

    this.getOptions(this.input.value).then((options) => {
      if (this.input.value.trim().length === 0 && options.length === 0) {
        return;
      }

      this.buildMenu(options);
      this.updateStatus(
        this.grouped ? options.reduce((acc, group) => acc + group.results.length, 0) : options.length,
      );
      this.showMenu();
    });
  };

  textBoxKeydownHandler = (e) => {
    switch (e.key) {
      case 'Tab':
        this.hideMenu();
        this.removeTextBoxFocus();
        break;
      default:
        break;
    }
  };

  textBoxKeyupHandler = (e) => {
    switch (e.key) {
      case 'Escape':
      case 'ArrowUp':
      case 'ArrowLeft':
      case 'ArrowRight':
      case ' ':
      case 'Enter':
      case 'Tab':
      case 'Shift':
        // 👆 ignore these keys, otherwise the menu will show briefly
        break;
      case 'ArrowDown':
        this.onTextBoxDownPressed(e);
        break;
      default:
        this.onTextBoxType(e);
    }
  };

  menuKeyDownHandler = (e) => {
    switch (e.key) {
      case 'ArrowUp':
        // want to highlight previous option
        this.onOptionUpArrow(e);
        break;
      case 'ArrowDown':
        // want to highlight next suggestion
        this.onOptionDownArrow(e);
        break;
      case 'Enter':
        // want to select the suggestion
        this.onOptionEnter(e);
        break;
      case ' ':
        // want to select the suggestion
        this.onOptionSpace(e);
        break;
      case 'Escape':
        // want to hide options
        this.onOptionEscape(e);
        break;
      case 'Tab':
        this.hideMenu();
        this.removeTextBoxFocus();
        break;
      default:
        this.input.focus();
    }
  };

  menuClickHandler = (e) => {
    const option = e.target;

    if (e.target.getAttribute('role') === 'option') {
      this.selectOption(option);
    }

    // Sometimes clicking on the text inside of an option will trigger an inner div
    const closestAncestorOption = e.target.closest('[role="option"]');

    if (closestAncestorOption) {
      this.selectOption(closestAncestorOption);
    }
  };

  onTextBoxDownPressed = () => {
    let option;
    const value = this.input.value.trim();
    // Empty value, then show all the options
    // if (value.length === 0) {
    //   options = this.getAllOptions();
    //   this.buildMenu(options);
    //   this.showMenu();
    //   option = this.getFirstOption();
    //   this.highlightOption(option);
    // }

    if (value.length > 0) {
      if (this.querySelector(['[role="option"]'])) {
        this.showMenu();
        option = this.getFirstOption();
        this.highlightOption(option);
      } else {
        this.getOptions(this.input.value.trim().toLowerCase()).then((options) => {
          if (options.length > 0) {
            this.buildMenu(options);
            this.showMenu();
            option = this.getFirstOption();
            this.highlightOption(option);
          }
        });
      }
    }
  };

  onTextBoxType = () => {
    if (this.input.value.trim().length > 0) {
      clearTimeout(this.debounce);
      this.showClearButton();

      this.debounce = setTimeout(() => {
        const query = this.input.value.trim().toLowerCase();

        this.emit('type', { query });

        // Clear out any previously selected category data
        if (this.entityIdInputName) {
          this.querySelector(`input[name="${this.entityIdInputName}"]`).value = '';
          this.querySelector(`input[name="find_type"]`).value = '';
        }

        this.getOptions(query).then((options) => {
          if (this.querySelector('.input-group').getAttribute('data-focused') === 'true') {
            this.buildMenu(options);
            this.showMenu();
            this.updateStatus(
              this.grouped ? options.reduce((acc, group) => acc + group.results.length, 0) : options.length,
            );
          }
        });
      }, 400);
    } else {
      this.hideMenu();
      this.hideClearButton();
    }
  };

  getFirstOption = () => {
    return this.querySelector('[role="option"]');
  };

  highlightOption = (option) => {
    if (this.activeOptionId) {
      const activeOption = this.querySelector(`#${this.activeOptionId}`);
      activeOption.setAttribute('aria-selected', 'false');
    }

    option.setAttribute('aria-selected', 'true');

    if (!this.isElementVisible(this.querySelector('[role="listbox"]'), option)) {
      this.querySelector('[role="listbox"]').scrollTop =
        this.querySelector('[role="listbox"]').scrollTop + option.offsetTop;
    }

    this.activeOptionId = option.id;
    option.focus();
  };

  getAllOptions = () => {
    return [];
  };

  clearOptions = () => {
    this.querySelector('[role="listbox"]').innerHTML = '';
  };

  focusHandler = () => {
    this.showMenu();
  };

  isElementVisible = (container, element) => {
    const containerHeight = container.clientHeight;
    const elementTop = element.getBoundingClientRect().top + window.scrollY;
    const containerTop = container.getBoundingClientRect().top + window.scrollY;
    const elementPaddingTop = Number.parseInt(getComputedStyle(element).getPropertyValue('padding-top'), 10);
    const elementPaddingBottom = Number.parseInt(getComputedStyle(element).getPropertyValue('padding-bottom'), 10);
    const elementHeight = element.clientHeight + elementPaddingTop + elementPaddingBottom;
    let visible;

    if (elementTop - containerTop < 0 || elementTop - containerTop + elementHeight > containerHeight) {
      visible = false;
    } else {
      visible = true;
    }
    return visible;
  };

  buildMenu = (options) => {
    this.clearOptions();
    this.activeOptionId = null;

    let groupedIndex = 0;

    if (options.length > 0) {
      for (let i = 0; i < options.length; i++) {
        if (this.grouped) {
          const groupLabelId = `groupLabel-${i + 1}`;
          const group = document.createElement('ul');
          group.setAttribute('aria-labelledby', groupLabelId);
          group.setAttribute('role', 'group');
          group.innerHTML = `<li id="${groupLabelId}" role="presentation">${options[i].category}</li>`;
          this.querySelector('[role="listbox"]').append(group);

          for (let j = 0; j < options[i].results.length; j++) {
            group.append(this.getOption(groupedIndex, options[i].results[j]));
            groupedIndex += 1;
          }
        } else {
          this.querySelector('[role="listbox"]').append(this.getOption(i, options[i]));
        }
      }
    } else {
      this.querySelector('[role="listbox"]').append(this.getNoResultsOptionHtml());
    }

    this.querySelector('[role="listbox"]').scrollTop = 0;
  };

  getNoResultsOptionHtml = () => {
    const li = document.createElement('li');
    li.className = 'no-results';
    li.innerText = 'No results';
    return li;
  };

  getOption = (index, text) => {
    const option = this.getOptionAttributes(text, this.input.value);

    const li = document.createElement('li');
    li.setAttribute('role', 'option');
    li.setAttribute('aria-selected', 'false');
    li.id = `${this.input.id}-${index + 1}`;
    li.innerHTML = option.html;
    li.tabIndex = -1;

    if (option.attributes) {
      for (const attribute of option.attributes) {
        li.setAttribute(attribute.name, attribute.value);
      }
    }

    return li;
  };

  onOptionDownArrow = (e) => {
    const option = this.getNextOption();

    if (option) {
      this.highlightOption(option);
    }

    e.preventDefault();
  };

  onOptionUpArrow = (e) => {
    if (this.activeOptionId) {
      const option = this.getPreviousOption();
      if (option) {
        this.highlightOption(option);
      } else {
        this.input.focus();
        this.hideMenu();
      }
    }

    e.preventDefault();
  };

  onOptionEnter = (e) => {
    if (this.activeOptionId) {
      this.selectActiveOption();
    }
    // we don't want form to submit
    e.preventDefault();
  };

  onOptionSpace = (e) => {
    if (this.activeOptionId) {
      this.selectActiveOption();
      // we don't want a space to be added to text box
      e.preventDefault();
    }
  };

  onOptionEscape = (e) => {
    e.preventDefault(); // if autocomplete exists in dialog, this prevents bubbling which would close dialog

    this.clearOptions();
    this.hideMenu();
    this.input.focus();
  };

  getPreviousOption = () => {
    const previousOption = this.querySelector(`#${this.activeOptionId}`).previousSibling;

    if (!this.grouped) {
      return previousOption;
    }

    // 👇 ignore group headings, try to get previous list instead
    if (previousOption && previousOption.getAttribute('role') === 'presentation') {
      const previousListbox = this.querySelector(`#${this.activeOptionId}`).parentNode.previousSibling;
      return previousListbox?.querySelector('[role="option"]:last-of-type');
    }

    return previousOption;
  };

  getNextOption = () => {
    const nextOption = this.querySelector(`#${this.activeOptionId}`).nextSibling;

    if (!this.grouped) {
      return nextOption;
    }

    if (!nextOption) {
      const nextListbox = this.querySelector(`#${this.activeOptionId}`).parentNode.nextSibling;
      return nextListbox?.querySelector('[role="option"]');
    }

    return nextOption;
  };

  showMenu = () => {
    this.querySelector('[role="listbox"]').style.display = 'block';
    this.input.setAttribute('aria-expanded', 'true');
  };

  hideMenu = () => {
    this.querySelector('[role="listbox"]').style.display = 'none';
    this.input.setAttribute('aria-expanded', 'false');
  };

  selectActiveOption = () => {
    this.selectOption(this.querySelector(`#${this.activeOptionId}`));
  };

  selectOption = (option) => {
    const isOrganization = option.hasAttribute('data-url');
    const value = option.getAttribute('data-value') || option.innerText;

    this.emit('select-option', { value, isOrganization });

    this.input.value = option.hasAttribute('data-title') ? option.getAttribute('data-title') : value;

    this.hideMenu();
    this.input.focus();

    if (isOrganization) {
      window.location.href = option.getAttribute('data-url');
    }

    if (this.entityIdInputName) {
      if (option.hasAttribute('data-entity-id')) {
        this.querySelector(`input[name="${this.entityIdInputName}"]`).value =
          option.getAttribute('data-entity-id');
        this.querySelector(`input[name="find_type"]`).value = 'Category';
      } else if (this.entityIdInputName) {
        this.querySelector(`input[name="${this.entityIdInputName}"]`).value = '';
        this.querySelector(`input[name="find_type"]`).value = '';
      }
    }
  };

  clearInput = () => {
    this.input.value = '';
    this.clearOptions();
    this.hideMenu();
    this.input.focus();

    const clrBtn = this.querySelector('button.clear');
    clrBtn.style.display = 'none';
  };
}
